import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'

/**
 * Rapoartele unei acțiuni de sampling.
 *
 * Cerute de Adela, iar Victor le-a dat forma cu întrebarea „îmi poate face un rezumat câte kituri
 * am dat versus cele care se anulează că nu au venit după ele?".
 *
 * Fiecare bloc răspunde la o întrebare pusă cu voce tare în cele două zile de eveniment. N-am pus
 * niciun număr care să nu răspundă la ceva — un tablou de bord plin de cifre pe care nimeni nu le-a
 * cerut se citește o dată, la lansare, și niciodată după.
 */

interface Rapoarte {
  actiune: { id: number; denumire: string; tip: string; locatie: string | null }
  palnie: {
    inscrisi: number
    inscrisiAnulati: number
    cadouCerutInFormular: number
    cadouPredat: number
    premiiExtrase: number
    premiiPredate: number
    premiiNeridicate: number
    dateSterse: number
  }
  produse: { sku: string; denumire: string; rol: string; bucati: number; predari: number }[]
  peOra: { ziOra: string; inscrieri: number }[]
  extrageri: {
    nr: number
    oraFinal: string
    eReextragere: boolean
    catiEligibili: number | null
    nrExtras: number
    castigator: string
    predat: boolean
    neridicatLa: string | null
    minutePanaLaPredare: number | null
  }[]
  dubluri: {
    nume: string
    telefonApropiat: boolean
    inscrieri: {
      nrPv: string
      inscrisLa: string
      telefonFinal: string | null
      emailDomeniu: string | null
      aPrimitCadou: boolean
      aCastigat: boolean
    }[]
  }[]
  marketing: { email: number; sms: number; ambele: number; niciunul: number; revocate: number }
  documente: {
    emailTrimis: number
    emailEsuat: number
    bonTrimis: number
    bonEsuat: number
    bonOprit: number
    bonStornat: number
  }
}

interface ActiuneScurta {
  id: number
  denumire: string
  tip: string
}

function ora(iso: string): string {
  return new Intl.DateTimeFormat('ro-RO', {
    timeZone: 'Europe/Bucharest',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso))
}

/** Un număr mare, cu ce înseamnă scris sub el. */
function Cifra({ n, eticheta, ton }: { n: number; eticheta: string; ton?: 'bun' | 'atent' }) {
  const culoare =
    ton === 'bun'
      ? 'text-b4l-green-dark'
      : ton === 'atent'
        ? 'text-b4l-amber-dark'
        : 'text-b4l-text'
  return (
    <div className="rounded-lg border border-b4l-border p-3">
      <div className={`text-2xl font-semibold tabular-nums ${culoare}`}>{n}</div>
      <div className="mt-0.5 text-xs text-b4l-text-muted">{eticheta}</div>
    </div>
  )
}

export function SamplingRapoarte() {
  const actiuni = useQuery({
    queryKey: ['sampling', 'actiuni-rapoarte'],
    /*
      Endpointul intoarce direct vectorul, nu `{ items }`. Prima varianta cerea `.items` si primea
      `undefined`, deci selectorul ramanea gol si nu se incarca niciun raport.
    */
    queryFn: async () =>
      (
        await api.get<ActiuneScurta[]>('/marketing/sampling/actiuni', {
          params: { stare: 'toate' },
        })
      ).data,
  })
  const [actiuneId, setActiuneId] = useState<number | ''>('')
  const aleasa = actiuneId === '' ? (actiuni.data?.[0]?.id ?? null) : actiuneId

  const r = useQuery({
    queryKey: ['sampling', 'rapoarte', aleasa],
    enabled: aleasa != null,
    queryFn: async () =>
      (await api.get<Rapoarte>(`/marketing/sampling/actiuni/${aleasa}/rapoarte`)).data,
  })

  const maxOra = Math.max(1, ...(r.data?.peOra.map((x) => x.inscrieri) ?? [1]))

  return (
    <div className="space-y-4">
      {/* Pe telefon, selectorul ocupa toata latimea: un camp ingust cu denumiri lungi nu se citeste. */}
      <select
        value={aleasa ?? ''}
        onChange={(e) => setActiuneId(e.target.value ? Number(e.target.value) : '')}
        className="w-full rounded-lg border border-b4l-border bg-white px-3 py-2 text-sm sm:w-auto"
      >
        {(actiuni.data ?? []).map((a) => (
          <option key={a.id} value={a.id}>
            {a.denumire}
          </option>
        ))}
      </select>

      {r.isLoading && <p className="text-sm text-b4l-text-muted">Se calculează…</p>}

      {r.data && (
        <>
          {/*
            Pâlnia. Răspunsul direct la întrebarea lui Victor: câte s-au dat și câte s-au pierdut
            pe drum. Numerele sunt puse în ordinea în care se pierde lumea, nu în ordine alfabetică.
          */}
          <Card title="De la înscriere la marfa dată">
            <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-4">
              <Cifra n={r.data.palnie.inscrisi} eticheta="înscrieri valide" />
              <Cifra
                n={r.data.palnie.cadouCerutInFormular}
                eticheta="au cerut cadoul în formular"
              />
              <Cifra n={r.data.palnie.cadouPredat} eticheta="au primit cadoul" ton="bun" />
              <Cifra n={r.data.palnie.inscrisiAnulati} eticheta="înscrieri anulate" ton="atent" />
              <Cifra n={r.data.palnie.premiiExtrase} eticheta="premii extrase" />
              <Cifra n={r.data.palnie.premiiPredate} eticheta="premii predate" ton="bun" />
              <Cifra
                n={r.data.palnie.premiiNeridicate}
                eticheta="premii neridicate, reportate"
                ton="atent"
              />
              <Cifra n={r.data.palnie.dateSterse} eticheta="au cerut ștergerea datelor" />
            </div>
            <p className="px-3 pb-3 text-xs text-b4l-text-muted">
              Cererea cadoului din formular e o intenție, nu o predare: mulți bifează și nu mai trec
              pe la stand, alții nu bifează și îl cer acolo. Diferența dintre cele două numere spune
              cât de bine a funcționat mecanismul Story.
            </p>
          </Card>

          {/*
            Marfa. E singurul raport care se compară cu Expert — de aceea are SKU-ul lângă denumire
            și numără bucăți, nu rânduri.
          */}
          <Card title="Ce a ieșit din gestiune">
            {r.data.produse.length === 0 ? (
              <p className="p-3 text-sm text-b4l-text-muted">Nicio predare încă.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[32rem] text-sm">
                  <thead>
                    <tr className="border-b border-b4l-border text-left text-xs text-b4l-text-muted">
                      <th className="px-3 py-2 font-medium">Produs</th>
                      <th className="px-3 py-2 font-medium">SKU</th>
                      <th className="px-3 py-2 font-medium">Rol</th>
                      <th className="px-3 py-2 text-right font-medium">Bucăți</th>
                      <th className="px-3 py-2 text-right font-medium">Predări</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.data.produse.map((p) => (
                      <tr
                        key={`${p.sku}-${p.rol}`}
                        className="border-b border-b4l-border last:border-0"
                      >
                        <td className="px-3 py-2">{p.denumire}</td>
                        <td className="px-3 py-2 font-mono text-xs text-b4l-text-muted">{p.sku}</td>
                        <td className="px-3 py-2 text-xs">
                          {p.rol === 'premiu' ? 'premiu tombolă' : 'recompensă story'}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums">
                          {p.bucati}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-b4l-text-muted">
                          {p.predari}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/*
            Fluxul pe ore. Nu e o curiozitate: spune câți oameni trebuie la stand data viitoare și
            la ce oră. Bara e desenată din lățime, nu dintr-o bibliotecă de grafice — la zece rânduri
            n-are rost să încărcăm pagina cu una.
          */}
          <Card title="Când s-au înscris">
            <div className="space-y-1 p-3">
              {r.data.peOra.map((x) => (
                <div key={x.ziOra} className="flex items-center gap-2 text-xs">
                  <span className="w-24 shrink-0 font-mono text-b4l-text-muted">{x.ziOra}</span>
                  <div
                    className="h-4 rounded bg-b4l-blue"
                    style={{ width: `${Math.max(2, (x.inscrieri / maxOra) * 100)}%` }}
                  />
                  <span className="tabular-nums text-b4l-text">{x.inscrieri}</span>
                </div>
              ))}
            </div>
          </Card>

          {/*
            Extragerile, cu soarta fiecărui premiu. Coloana de minute se citește față de cele două
            ore din art. 6.2: peste ani, ea spune dacă termenul a fost respectat, fără să mai
            socotească nimeni din două ore scrise separat.
          */}
          {r.data.extrageri.length > 0 && (
            <Card title="Extragerile și soarta premiilor">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[32rem] text-sm">
                  <thead>
                    <tr className="border-b border-b4l-border text-left text-xs text-b4l-text-muted">
                      <th className="px-3 py-2 font-medium">Nr.</th>
                      <th className="px-3 py-2 font-medium">Ora</th>
                      <th className="px-3 py-2 font-medium">Câștigător</th>
                      <th className="px-3 py-2 text-right font-medium">Bază</th>
                      <th className="px-3 py-2 font-medium">Stare</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.data.extrageri.map((e) => (
                      <tr key={e.nr} className="border-b border-b4l-border last:border-0">
                        <td className="px-3 py-2 tabular-nums">
                          {e.nr}
                          {e.eReextragere && (
                            <span className="ml-1 text-xs text-b4l-text-muted">(re)</span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{ora(e.oraFinal)}</td>
                        <td className="px-3 py-2">
                          {e.castigator}{' '}
                          <span className="font-mono text-xs text-b4l-text-muted">
                            #{e.nrExtras}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-b4l-text-muted">
                          {e.catiEligibili ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {e.predat ? (
                            <span className="text-b4l-green-dark">predat</span>
                          ) : e.neridicatLa ? (
                            <span className="text-b4l-amber-dark">
                              neridicat
                              {e.minutePanaLaPredare != null &&
                                ` · după ${Math.floor(e.minutePanaLaPredare / 60)}h ${
                                  e.minutePanaLaPredare % 60
                                }m`}
                            </span>
                          ) : (
                            <span className="text-b4l-text-muted">în așteptare</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/*
            Verificarea dublurilor. Alexandru, dupa ce a gasit treisprezece nume repetate: „adaugi
            undeva in meniu Verificare dubluri?".

            NU arata telefonul si emailul. In Contacte sunt mascate si se cer cu parola, cu cererea
            scrisa in jurnal; un raport care le-ar afisa liber ar ocoli exact protectia aceea. Arata
            ultimele trei cifre si domeniul adresei — atat cat sa recunosti omul, nu cat sa-l suni.
          */}
          {r.data.dubluri.length > 0 && (
            <Card title={`Posibile dubluri (${r.data.dubluri.length})`}>
              <p className="px-3 pt-3 text-xs text-b4l-text-muted">
                Același nume, contacte diferite. Aplicația nu poate decide: un nume nu e
                identificator, iar telefonul și emailul chiar diferă. Cele marcate au numere care
                diferă prin cel mult două cifre — acolo e aproape sigur aceeași persoană, înscrisă
                de două ori.
              </p>
              <div className="divide-y divide-b4l-border p-3">
                {r.data.dubluri.map((d) => (
                  <div key={d.nume} className="py-2 first:pt-0 last:pb-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-b4l-text">{d.nume}</span>
                      {d.telefonApropiat && (
                        <span className="rounded-full bg-b4l-amber-light px-2 py-0.5 text-xs text-b4l-amber-dark">
                          telefoane aproape identice
                        </span>
                      )}
                    </div>
                    <ul className="mt-1 space-y-0.5">
                      {d.inscrieri.map((i) => (
                        <li key={i.nrPv} className="text-xs text-b4l-text-muted">
                          <span className="font-mono">{i.nrPv}</span> · {ora(i.inscrisLa)} · telefon
                          …{i.telefonFinal ?? '—'} · {i.emailDomeniu ?? 'fără email'}
                          {i.aPrimitCadou && (
                            <span className="ml-1 text-b4l-green-dark">· a primit cadoul</span>
                          )}
                          {i.aCastigat && (
                            <span className="ml-1 text-b4l-amber-dark">· a câștigat</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="px-3 pb-3 text-xs text-b4l-text-muted">
                Numărul întreg și adresa se cer din Contacte, cu parola — și rămân scrise în jurnal,
                cu numele celui care le-a cerut.
              </p>
            </Card>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            {/*
              Acordurile de marketing. Ăsta e, de fapt, rezultatul comercial al campaniei: kiturile
              se dau o dată, lista rămâne. Se numără pe OM, nu pe înscriere.
            */}
            <Card title="Acorduri de marketing">
              <div className="grid grid-cols-2 gap-2 p-3">
                <Cifra n={r.data.marketing.ambele} eticheta="email și SMS" ton="bun" />
                <Cifra n={r.data.marketing.email} eticheta="doar email" ton="bun" />
                <Cifra n={r.data.marketing.sms} eticheta="doar SMS" />
                <Cifra n={r.data.marketing.niciunul} eticheta="fără acord" />
              </div>
              {r.data.marketing.revocate > 0 && (
                <p className="px-3 pb-3 text-xs text-b4l-text-muted">
                  {r.data.marketing.revocate} acorduri retrase după înscriere.
                </p>
              )}
            </Card>

            {/*
              Documentele. Aici se vede ce a rămas în urmă — un bon eșuat înseamnă marfă ieșită fără
              document, iar aia se descoperă altfel abia la inventar.
            */}
            <Card title="Documente">
              <div className="grid grid-cols-2 gap-2 p-3">
                <Cifra n={r.data.documente.emailTrimis} eticheta="e-mailuri trimise" ton="bun" />
                <Cifra
                  n={r.data.documente.emailEsuat}
                  eticheta="e-mailuri eșuate"
                  ton={r.data.documente.emailEsuat > 0 ? 'atent' : undefined}
                />
                <Cifra n={r.data.documente.bonTrimis} eticheta="bonuri în EA" ton="bun" />
                <Cifra
                  n={r.data.documente.bonEsuat + r.data.documente.bonOprit}
                  eticheta="bonuri eșuate sau oprite"
                  ton={
                    r.data.documente.bonEsuat + r.data.documente.bonOprit > 0 ? 'atent' : undefined
                  }
                />
              </div>
              {r.data.documente.bonStornat > 0 && (
                <p className="px-3 pb-3 text-xs text-b4l-text-muted">
                  {r.data.documente.bonStornat} bonuri stornate în ExpertAccounts.
                </p>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  )
}
